import { useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { isEqual } from 'lodash';
import { RootState } from 'state/store';
import { selectCapacityFilters } from 'state/Capacities/Filters/filtersSlice';
import { fetchTeamMembersList } from 'state/Capacities/TeamMembersList/teamMembersListSlice';
import {
  MembersByTeam,
  TeamMemberWithLinks,
} from 'utils/types/dailyTeamsCapacity';
import useCapacityCancellationContext from 'Pages/TeamsPage/views/TeamsCapacity/components/CapacityTable/context/useCapacityCancellationContext';

const BATCH_SIZE = 6;

const useRefetchTeamMembersListOnFiltersChange = (): void => {
  const dispatch = useDispatch();
  const { getCancelToken, cancelInFlightRequests } =
    useCapacityCancellationContext();
  const filters = useSelector(selectCapacityFilters);
  const expandedTeams = useSelector(
    (state: RootState) => state.capacities.teamMembersList.byTeam
  );

  const prevFiltersRef = useRef(filters);
  const filterVersionRef = useRef(0);
  const expandedTeamsRef = useRef<MembersByTeam<TeamMemberWithLinks>>({});
  expandedTeamsRef.current = expandedTeams;

  useEffect(() => {
    if (isEqual(prevFiltersRef.current, filters)) {
      return;
    }

    prevFiltersRef.current = filters;
    filterVersionRef.current++;
    cancelInFlightRequests();
    const currentVersion = filterVersionRef.current;

    const refetch = async () => {
      try {
        const requests: string[] = [];
        for (const teamId of Object.keys(expandedTeamsRef.current)) {
          requests.push(teamId);
        }

        for (let i = 0; i < requests.length; i += BATCH_SIZE) {
          if (currentVersion !== filterVersionRef.current) return;
          const batch = requests.slice(i, i + BATCH_SIZE);
          const batchPromises = batch.map((teamId) =>
            dispatch(
              fetchTeamMembersList({
                teamId,
                force: true,
                cancelToken: getCancelToken(),
              }) as any
            ).unwrap()
          );

          await Promise.all(batchPromises);
        }
      } catch {
        // cancelled or rejected — chain stops
      }
    };

    refetch();
  }, [filters, dispatch, getCancelToken, cancelInFlightRequests]);
};

export default useRefetchTeamMembersListOnFiltersChange;
